'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import { apiService } from '@/lib/api-client';
import { AuctionDetail } from '@/lib/types/api';
import { convertUTCToLocal, formatDateTime } from '@/lib/utils';
import { useAuctionSocket, Bid, Auction } from '@/lib/hooks/useAuctionSocket';
import { useFavorites } from '@/lib/hooks/useFavorites';


// Global window tipi tanımı
declare global {
  interface Window {
    showNotification?: {
      success: (message: string) => void;
      error: (message: string) => void;
      info: (message: string) => void;
      warning: (message: string) => void;
    };
  }
}

interface AuctionDetailClientProps {
  auctionSlug: string;
  locale: string;
  initialAuctionData: AuctionDetail | null;
  initialAuthState: {
    isAuthenticated: boolean;
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
      isEDevletVerified?: boolean;
      roles?: string[];
    } | null;
  };
}

export default function AuctionDetailClient({ 
  auctionSlug, 
  locale, 
  initialAuctionData,
  initialAuthState
}: AuctionDetailClientProps) {
  const t = useTranslations('auctionDetail');
  const tNotifications = useTranslations('notifications');
  const { toggleFavorite, isLoading: favoriteLoading } = useFavorites();
  const [auction, setAuction] = useState<AuctionDetail | null>(initialAuctionData);
  const [isFavorite, setIsFavorite] = useState<boolean>(() => {
    return initialAuctionData?.isFavorite || false;
  });
  // Eğer resim yoksa video seçili gelsin, yoksa ilk resim seçili gelsin
  const [selectedImage, setSelectedImage] = useState(() => {
    if (initialAuctionData?.media) {
      const hasImages = initialAuctionData.media.images && initialAuctionData.media.images.length > 0;
      const hasVideo = initialAuctionData.media.video && initialAuctionData.media.video.trim() !== '';
      
      // Eğer resim yoksa ve video varsa, video seçili gelsin
      if (!hasImages && hasVideo) {
        return -1; // Video seçili
      }
    }
    return 0; // İlk resim seçili veya varsayılan
  });
  const [bidAmount, setBidAmount] = useState(() => {
    // İlk açılışta default teklif değerini hesapla
    if (initialAuctionData) {
      if (initialAuctionData.currentHighestBid) {
        // Teklif verilmiş - en yüksek teklif + minimum artış
        return initialAuctionData.currentHighestBid + initialAuctionData.minimumIncrement;
      } else {
        // Henüz teklif verilmemiş - başlangıç fiyatı + minimum artış
        return initialAuctionData.startingPrice + initialAuctionData.minimumIncrement;
      }
    }
    return 0;
  });
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [timeUntilStart, setTimeUntilStart] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bidResult, setBidResult] = useState<'pending' | 'success' | 'error' | null>(null);
  const [bidResultMessage, setBidResultMessage] = useState<string>('');
  const [isHighestBidder, setIsHighestBidder] = useState<boolean>(() => {
    // İlk açılışta API'den gelen veriyi kullan
    return initialAuctionData?.isHighestBidder || false;
  });
  const [isPropertyDetailsOpen, setIsPropertyDetailsOpen] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  

  const [isLoading, setIsLoading] = useState(!initialAuctionData);
  const [error, setError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  // SSR'dan gelen auth durumunu kullan - flash efektini önle
  const [authState] = useState({
    isAuthenticated: initialAuthState.isAuthenticated,
    user: initialAuthState.user
  });

  // Structured Data (JSON-LD) için useEffect
  useEffect(() => {
    if (!auction) return;

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: auction.title,
      description: auction.description || auction.metaDescription || '',
      image: auction.media?.images || [],
      offers: {
        '@type': 'Offer',
        price: auction.currentHighestBid || auction.startingPrice,
        priceCurrency: 'TRY',
        availability: auction.status === 2 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: typeof window !== 'undefined' ? window.location.href : '',
      },
      ...(auction.address && {
        location: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: auction.address,
          },
        },
      }),
    };

    // Mevcut structured data script'ini kaldır
    const existingScript = document.getElementById('structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    // Yeni structured data script'ini ekle
    const script = document.createElement('script');
    script.id = 'structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [auction]);

  // Socket hook - SSR auth durumunu kullan, E-Devlet doğrulaması kontrol et ve sadece devam eden müzayedelerde bağlan
  const shouldConnectSocket = useMemo(() => {
    return authState.isAuthenticated 
      && authState.user?.isEDevletVerified === true 
      && !authState.user?.roles?.includes('Admin')
      && auction?.status === 2; // Sadece devam eden müzayedelerde bağlan
  }, [authState.isAuthenticated, authState.user?.isEDevletVerified, authState.user?.roles, auction?.status]);
  const {
    isConnected: socketConnected,
    isConnecting: socketConnecting,
    error: socketError,
    isAuthenticated: socketAuthenticated,
    placeBid: socketPlaceBid,
    setOnNewBid,
    setOnAuctionStatusUpdated,
    setOnBidPlacedSuccess,
    setOnBidPlacedError,
  } = useAuctionSocket(initialAuctionData?.id || '', shouldConnectSocket);

  // Müzayede verilerini yükle
  const loadAuctionData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.auctions.getBySlug(auctionSlug);
      
      if (response.isSuccess && response.data) {
        setAuction(response.data);
        // isHighestBidder bilgisini güncelle
        setIsHighestBidder(response.data.isHighestBidder);
        // isFavorite bilgisini güncelle
        setIsFavorite(response.data.isFavorite);
        // Bid amount'u güncelle
        // Henüz teklif verilmemişse başlangıç fiyatı + minimum artış, verilmişse en yüksek teklif + minimum artış
        if (response.data.currentHighestBid) {
          // Teklif verilmiş - en yüksek teklif + minimum artış
          setBidAmount(response.data.currentHighestBid + response.data.minimumIncrement);
        } else {
          // Henüz teklif verilmemiş - başlangıç fiyatı + minimum artış
          setBidAmount(response.data.startingPrice + response.data.minimumIncrement);
        }
      } else {
        setError(response.message || t('loading.auctionLoadError'));
      }
    } catch (err) {
      // Error handling
      setError(t('loading.auctionLoadErrorGeneral'));
    } finally {
      setIsLoading(false);
    }
  }, [auctionSlug, t]);

  // Favori toggle fonksiyonu
  const handleFavoriteToggle = useCallback(async () => {
    if (!auction) return;
    
    try {
      const newFavoriteStatus = await toggleFavorite(auction.id, isFavorite);
      setIsFavorite(newFavoriteStatus);
    } catch (error) {
      // Error handling
    }
  }, [auction, isFavorite, toggleFavorite]);

  // Socket event handlers
  const handleNewBid = useCallback((bid: Bid) => {
    // Null/undefined kontrolü
    if (!bid) {
      return;
    }
    
    // Log removed
    
    // Global notification göster - setTimeout ile asenkron hale getir
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.showNotification) {
        window.showNotification.info(tNotifications('newBidPlaced'));
      }
    }, 0);
    
    // Socket'ten gelen veriyi doğrudan state'e uygula
    setAuction(prevAuction => {
      if (!prevAuction) return prevAuction;
      
      return {
        ...prevAuction,
        currentHighestBid: bid.currentHighestBid,
        totalBidCount: bid.totalBidCount,
        endDate: bid.endDate
      };
    });
    
    // En yüksek teklif sahibi bilgisini güncelle
    setIsHighestBidder(bid.isHighestBidder);
    
    // Bid amount'u güncelle - currentHighestBid + minimumIncrement
    setBidAmount(prevAmount => {
      const currentBid = bid.currentHighestBid;
      const increment = auction?.minimumIncrement || 0;
      const newAmount = currentBid + increment;
      return Math.max(newAmount, prevAmount);
    });
  }, [tNotifications, auction]);

  const handleAuctionStatusUpdated = useCallback((auction: Auction) => {
    // Null/undefined kontrolü
    if (!auction) {
      return;
    }
    
    // Global notification göster - setTimeout ile asenkron hale getir
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.showNotification) {
        window.showNotification.info(tNotifications('auctionStatusUpdated'));
      }
    }, 0);
    
    // Socket'ten gelen veriyi doğrudan state'e uygula
    setAuction(prevAuction => {
      if (!prevAuction) return prevAuction;
      
      return {
        ...prevAuction,
        status: auction.status === 'Active' ? 2 : auction.status === 'Completed' ? 4 : 1,
        currentHighestBid: auction.currentPrice || null,
        endDate: auction.endDate,
        winnerUserName: auction.winner?.userName || null,
        winningBidAmount: auction.winner?.amount || null
      };
    });
  }, [tNotifications]);


  const handleBidPlacedSuccess = useCallback((bid: Bid) => {
    // Null/undefined kontrolü
    if (!bid) {
      return;
    }
    
    // Socket'ten gelen veriyi doğrudan state'e uygula
    setAuction(prevAuction => {
      if (!prevAuction) return prevAuction;
      
      return {
        ...prevAuction,
        currentHighestBid: bid.currentHighestBid,
        totalBidCount: bid.totalBidCount
      };
    });
    
    // En yüksek teklif sahibi bilgisini güncelle
    setIsHighestBidder(bid.isHighestBidder);
    
    // Bid amount'u güncelle
    setBidAmount(prevAmount => {
      const newAmount = bid.currentHighestBid + (auction?.minimumIncrement || 0);
      return Math.max(newAmount, prevAmount);
    });

    // Başarılı teklif sonrası dialog'u hemen kapat
    setShowConfirmModal(false);
    setBidResult(null);
    setBidResultMessage('');
    setIsSubmitting(false);
  }, [auction]);

  const handleBidPlacedError = useCallback((error: string) => {
    // Sadece gerçek hata mesajlarını göster
    if (!error || error === 'null' || error.trim() === '') {
      return; // Boş veya null hata mesajlarını gösterme
    }
    
    // Hata mesajını belirle
    let errorMessage = tNotifications('bidPlacedError');
    if (error && error !== 'null' && error.trim() !== '') {
      errorMessage = error;
    }
    
    // Modal'da hata durumunu göster
    setBidResult('error');
    setBidResultMessage(errorMessage);
    setIsSubmitting(false);
  }, [tNotifications]);



  // Set event handlers - useEffect içinde ayarla
  useEffect(() => {
    setOnNewBid(handleNewBid);
    setOnAuctionStatusUpdated(handleAuctionStatusUpdated);
    setOnBidPlacedSuccess(handleBidPlacedSuccess);
    setOnBidPlacedError(handleBidPlacedError);
  }, [
    setOnNewBid, 
    setOnAuctionStatusUpdated, 
    setOnBidPlacedSuccess, 
    setOnBidPlacedError, 
    handleNewBid,
    handleAuctionStatusUpdated,
    handleBidPlacedSuccess,
    handleBidPlacedError
  ]);

  // API'den müzayede verilerini yükle
  useEffect(() => {
    if (!initialAuctionData) {
      loadAuctionData();
    }
  }, [auctionSlug, initialAuctionData, loadAuctionData]);

  // Eğer resim yoksa video seçili gelsin - auction değiştiğinde kontrol et
  useEffect(() => {
    if (auction?.media) {
      const hasImages = auction.media.images && auction.media.images.length > 0;
      const hasVideo = auction.media.video && auction.media.video.trim() !== '';
      
      // Eğer şu anda seçili olan resim geçersizse veya resim yoksa ve video varsa, video seçili yap
      if (!hasImages && hasVideo && selectedImage !== -1) {
        setSelectedImage(-1); // Video seçili
      } else if (hasImages && selectedImage >= auction.media.images.length) {
        // Eğer seçili resim index'i geçersizse, ilk resme geç
        setSelectedImage(0);
      } else if (!hasImages && !hasVideo && selectedImage !== 0) {
        // Ne resim ne video varsa, varsayılan olarak 0
        setSelectedImage(0);
      }
    }
  }, [auction, selectedImage]);

  // Mobile detection ve responsive state management
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  // Kalan süreyi hesapla (aktif müzayedeler için)
  useEffect(() => {
    if (!auction || auction.status !== 2) return;

    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const endDate = convertUTCToLocal(auction.endDate).getTime();
      const timeLeft = endDate - now;

      if (timeLeft <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        
        // Geri sayım bittiğinde 1 saniye gecikme ile sayfa yenile
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        
        return;
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    };

    calculateTimeRemaining();
    const timer = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(timer);
  }, [auction]);

  // Başlama zamanına kalan süreyi hesapla (yaklaşan müzayedeler için)
  useEffect(() => {
    if (!auction || auction.status !== 1) return;

    const calculateTimeUntilStart = () => {
      const now = new Date().getTime();
      const startDate = convertUTCToLocal(auction.startDate).getTime();
      const timeLeft = startDate - now;

      if (timeLeft <= 0) {
        setTimeUntilStart({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        
        // Geri sayım bittiğinde 1 saniye gecikme ile sayfa yenile
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        
        return;
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      setTimeUntilStart({ days, hours, minutes, seconds });
    };

    calculateTimeUntilStart();
    const timer = setInterval(calculateTimeUntilStart, 1000);

    return () => clearInterval(timer);
  }, [auction]);

  // Fiyat formatlama fonksiyonu
  const formatPrice = (price: number) => {
    const formattedNumber = new Intl.NumberFormat(locale === 'tr' ? 'tr-TR' : 'en-US').format(price);
    return `₺${formattedNumber}`;
  };

  // Fiyat formatlama fonksiyonu - input için
  const formatInputPrice = (value: number) => {
    return new Intl.NumberFormat(locale === 'tr' ? 'tr-TR' : 'en-US').format(value);
  };

  // Formatlanmış fiyatı sayıya çevirme
  const parseFormattedPrice = (formattedValue: string) => {
    if (!formattedValue || formattedValue.trim() === '') {
      return 0;
    }
    const numericValue = Number(formattedValue.replace(/\D/g, ''));
    return isNaN(numericValue) ? 0 : numericValue;
  };

  // Teklif gönderme
  const handleBidSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  // Teklifi onayla
  const handleConfirmBid = async () => {
    if (!auction) return;
    
    setIsSubmitting(true);
    setBidResult('pending');
    setBidResultMessage('');
    
    try {
      // Socket üzerinden teklif gönder
      await socketPlaceBid(bidAmount);
      
      // Socket event handler'lar sonucu gösterecek
      // Bu yüzden burada herhangi bir state değişikliği yapmıyoruz
      
    } catch (error) {
      // Error handling
      
      // Hata mesajını belirle
      let errorMessage = tNotifications('bidPlacedError');
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string' && error.trim() !== '') {
        errorMessage = error;
      }
      
      // Hata durumunu modal'da göster
      setBidResult('error');
      setBidResultMessage(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleCancelBid = () => {
    setShowConfirmModal(false);
    setBidResult(null);
    setBidResultMessage('');
    setIsSubmitting(false);
  };

  const handleCloseResultModal = () => {
    setShowConfirmModal(false);
    setBidResult(null);
    setBidResultMessage('');
    setIsSubmitting(false);
  };

  // Scroll functions for image gallery
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };


  // Resim seçildiğinde - sadece videodan resme geçildiğinde ve video oynatılıyorsa durdur
  const handleImageSelect = (index: number) => {
    // Sadece şu anda video seçiliyse (selectedImage === -1) ve video oynatılıyorsa pause et
    if (selectedImage === -1 && videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    setSelectedImage(index);
  };

  // Video'ya geçildiğinde - video zaten DOM'da olduğu için currentTime korunuyor
  const handleVideoSelect = () => {
    setSelectedImage(-1);
  };

  // Lightbox controls
  const openLightbox = (index: number) => {
    if (!auction?.media?.images?.length) return;
    setLightboxIndex(index);
    setIsLightboxOpen(true);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  };

  const showPrev = () => {
    if (!auction?.media?.images?.length) return;
    setLightboxIndex((prev) => (prev - 1 + auction.media.images.length) % auction.media.images.length);
  };

  const showNext = () => {
    if (!auction?.media?.images?.length) return;
    setLightboxIndex((prev) => (prev + 1) % auction.media.images.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLightboxOpen, auction]);

  // Loading durumu
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading.auction')}</p>
        </div>
      </div>
    );
  }

  // Hata durumu
  if (error || !auction) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('loading.error')}</h2>
          <p className="text-gray-600 mb-4">{error || t('loading.auctionNotFound')}</p>
          <Link 
            href={`/${locale}/muzayedeler`}
            className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('loading.backToAuctions')}
          </Link>
        </div>
      </div>
    );
  }

  // Müzayede durumuna göre renk belirleme
  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return 'bg-blue-100 text-blue-800'; // Yaklaşan
      case 2: return 'bg-green-100 text-green-800'; // Aktif
      case 4: return 'bg-gray-100 text-gray-800'; // Tamamlanmış
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 1: return t('status.upcoming');
      case 2: return t('status.active');
      case 4: return t('status.completed');
      default: return t('status.unknown');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-4 sm:mb-6 lg:mb-8 text-sm">
          <Link href={`/${locale}/muzayedeler`} className="text-gray-600 hover:text-gray-900 transition-colors">
            {t('breadcrumb.auctions')}
          </Link>
          <span className="text-gray-600">/</span>
          <span className="text-gray-900 font-medium">{auction.title}</span>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
            {/* Main Media (Video or Image) */}
            <div className="relative h-96 lg:h-[480px] rounded-2xl overflow-hidden shadow-lg">
              {/* Video Player - Her zaman DOM'da tut, sadece görünürlüğünü değiştir */}
              {auction.media.video && auction.media.video.trim() !== '' && (
                <div className={`w-full h-full ${selectedImage === -1 ? 'block' : 'hidden'}`}>
                  <video
                    key={`video-${auction.id}`}
                    ref={videoRef}
                    src={auction.media.video}
                    controls
                    className="w-full h-full object-cover"
                    preload="metadata"
                  >
                    Tarayıcınız video oynatmayı desteklemiyor.
                  </video>
                </div>
              )}
              
              {/* Image Display */}
              <div className={`w-full h-full ${selectedImage >= 0 ? 'block' : 'hidden'}`}>
                {auction.media.images[selectedImage] && auction.media.images[selectedImage].trim() !== '' ? (
                  <Image
                    src={auction.media.images[selectedImage]}
                    alt={`${auction.title} - ${auction.address || 'Gayrimenkul'} müzayede görseli ${selectedImage + 1}`}
                    fill
                    className="object-cover cursor-pointer"
                    quality={70}
                    loading="lazy"
                    onClick={() => openLightbox(selectedImage)}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500">{t('media.imageNotFound')}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
              </div>
            </div>

            {/* Thumbnail Media */}
            {(auction.media.video || auction.media.images.length > 1) && (
              <div className="relative group">
                <div 
                  ref={scrollContainerRef}
                  className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 scroll-smooth"
                >
                  {/* Video Thumbnail */}
                  {auction.media.video && auction.media.video.trim() !== '' && (
                    <button
                      onClick={handleVideoSelect}
                      className={`relative flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === -1 
                          ? 'border-red-600 shadow-lg shadow-red-500/50' 
                          : 'border-white shadow-md hover:border-gray-300'
                      }`}
                    >
                      <video
                        key={`thumbnail-${auction.id}`}
                        src={auction.media.video}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                        playsInline
                        onLoadedMetadata={(e) => {
                          // Video yüklendiğinde ilk frame'i göster
                          const video = e.target as HTMLVideoElement;
                          video.currentTime = 1; // 1 saniye sonrasından thumbnail al
                        }}
                      />
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                        <svg className="w-8 h-8 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                      <div className="absolute top-1 right-1 bg-red-600 text-white text-xs px-1 py-0.5 rounded">
                        {t('media.video')}
                      </div>
                    </button>
                  )}
                  
                  {/* Image Thumbnails */}
                  {auction.media.images.map((image, index) => (
                    image && image.trim() !== '' && (
                      <button
                        key={`thumbnail-${index}`}
                        onClick={() => {
                          handleImageSelect(index);
                        }}
                        className={`relative flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedImage === index
                            ? 'border-red-600 shadow-lg shadow-red-500/50' 
                            : 'border-white shadow-md hover:border-gray-300'
                        }`}
                      >
                        <Image
                          src={image}
                          alt={`${auction.title} - ${auction.address || 'Gayrimenkul'} müzayede küçük resim ${index + 1}`}
                          fill
                          className="object-cover"
                          quality={60}
                          loading="lazy"
                        />
                      </button>
                    )
                  ))}
                </div>
                
                {/* Navigation buttons */}
                {((auction.media.video && auction.media.images.length > 3) || auction.media.images.length > 4) && (
                  <>
                    {/* Left navigation button */}
                    <button
                      onClick={scrollLeft}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20"
                      aria-label={t('media.scrollLeft')}
                    >
                      <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    {/* Right navigation button */}
                    <button
                      onClick={scrollRight}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20"
                      aria-label={t('media.scrollRight')}
                    >
                      <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
                
                {/* Scroll indicators */}
                {((auction.media.video && auction.media.images.length > 3) || auction.media.images.length > 4) && (
                  <>
                    {/* Left gradient indicator */}
                    <div className="absolute left-0 top-0 h-full bg-gradient-to-r from-white/60 via-white/30 to-transparent w-8 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {/* Right gradient indicator */}
                    <div className="absolute right-0 top-0 h-full bg-gradient-to-l from-white/60 via-white/30 to-transparent w-8 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                
                {/* Media counter */}
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full z-20">
                  {selectedImage === -1 ? t('media.video') : `${selectedImage + 1} / ${auction.media.images.length}`}
                </div>
              </div>
            )}

            {/* Lightbox Modal */}
            {isLightboxOpen && auction.media.images[lightboxIndex] && (
              <div 
                className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-2 sm:p-4" 
                role="dialog" 
                aria-modal="true"
                onClick={closeLightbox}
              >
                {/* Image Container with buttons */}
                <div 
                  className="relative w-full max-w-6xl h-[60vh] sm:h-[70vh] md:h-[80vh] flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close button */}
                  <button
                    onClick={closeLightbox}
                    aria-label={t('media.lightbox.close')}
                    className="absolute top-2 right-2 sm:top-0 sm:right-0 sm:-translate-y-12 text-white hover:text-white transition-colors z-20 bg-black/70 hover:bg-black/90 rounded-full p-2 sm:p-2 shadow-lg"
                  >
                    <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Prev button */}
                  <button
                    onClick={showPrev}
                    aria-label={t('media.lightbox.previous')}
                    className="absolute left-2 sm:left-0 sm:-translate-x-12 top-1/2 -translate-y-1/2 text-white hover:text-white transition-colors z-20 bg-black/70 hover:bg-black/90 rounded-full p-2 sm:p-3 shadow-lg"
                  >
                    <svg className="w-6 h-6 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Next button */}
                  <button
                    onClick={showNext}
                    aria-label={t('media.lightbox.next')}
                    className="absolute right-2 sm:right-0 sm:translate-x-12 top-1/2 -translate-y-1/2 text-white hover:text-white transition-colors z-20 bg-black/70 hover:bg-black/90 rounded-full p-2 sm:p-3 shadow-lg"
                  >
                    <svg className="w-6 h-6 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Image */}
                  <div className="relative w-full h-full">
                    <Image
                      src={auction.media.images[lightboxIndex]}
                      alt={`${auction.title} - ${lightboxIndex + 1}`}
                      fill
                      sizes="100vw"
                      className="object-contain select-none"
                      priority
                      quality={85}
                    />
                  </div>

                  {/* Counter */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-8 sm:translate-y-12 text-white/90 text-xs sm:text-sm bg-white/10 backdrop-blur px-2 py-1 sm:px-3 sm:py-1 rounded-full">
                    {t('media.lightbox.imageCounter', { current: lightboxIndex + 1, total: auction.media.images.length })}
                  </div>
                </div>
              </div>
            )}

            {/* Property Details */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg">
              {/* Mobile Accordion Header */}
              <button
                onClick={() => setIsPropertyDetailsOpen(!isPropertyDetailsOpen)}
                className="w-full p-4 sm:p-6 lg:p-8 text-left flex items-center justify-between hover:bg-gray-50 transition-colors lg:hidden"
              >
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('propertyDetails.title')}</h2>
                <svg 
                  className={`w-6 h-6 text-gray-500 transition-transform duration-200 ${
                    isPropertyDetailsOpen ? 'rotate-180' : ''
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Desktop Header */}
              <div className="hidden lg:block p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('propertyDetails.title')}</h2>
              </div>
              
              {/* Content - Mobile accordion or Desktop always visible */}
              <div className={`px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8 ${isMobile ? (isPropertyDetailsOpen ? 'block' : 'hidden') : 'block'}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {auction.properties.map((property, index) => (
                    <div key={index} className="border-b border-gray-200 pb-4">
                      <p className="text-gray-600 text-sm mb-1">{property.key}</p>
                      <p className="text-lg font-semibold text-gray-900">{property.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-lg">
              {/* Mobile Accordion Header */}
              <button
                onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
                className="w-full p-4 sm:p-6 lg:p-8 text-left flex items-center justify-between hover:bg-gray-50 transition-colors lg:hidden"
              >
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('description.title')}</h2>
                <svg 
                  className={`w-6 h-6 text-gray-500 transition-transform duration-200 ${
                    isDescriptionOpen ? 'rotate-180' : ''
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Desktop Header */}
              <div className="hidden lg:block p-4 sm:p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('description.title')}</h2>
              </div>
              
              {/* Content - Mobile accordion or Desktop always visible */}
              <div className={`px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8 ${isMobile ? (isDescriptionOpen ? 'block' : 'hidden') : 'block'}`}>
                <div 
                  className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: auction.description }}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Bidding Section */}
          <div className="space-y-4 sm:space-y-6">
            {/* Property Info */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(auction.status)}`}>
                    {getStatusText(auction.status)}
                  </span>
                  
                  {/* Socket Status Indicator - shouldConnectSocket koşuluna bağlı */}
                  {shouldConnectSocket && (
                    <div className="flex items-center">
                      {socketError ? (
                        <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      ) : socketConnecting ? (
                        <div className="w-4 h-4 bg-yellow-500 rounded-full animate-pulse"></div>
                      ) : socketConnected ? (
                        <div className="w-4 h-4 bg-green-500 rounded-full animate-[blink_3s_ease-in-out_infinite]"></div>
                      ) : (
                        <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Favori Butonu - Aktif etiketinin yanında */}
                {authState.isAuthenticated && (
                  <button
                    onClick={handleFavoriteToggle}
                    disabled={favoriteLoading}
                    className={`w-10 h-10 bg-white border-2 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm ${
                      isFavorite 
                        ? 'border-red-200 text-red-500 hover:bg-red-50' 
                        : 'border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-500 hover:bg-red-50'
                    } ${favoriteLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    title={isFavorite ? t('favorite.remove') : t('favorite.add')}
                  >
                    {favoriteLoading ? (
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg 
                        className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} 
                        fill={isFavorite ? 'currentColor' : 'none'} 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
              

              
              <h1 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">
                {auction.title}
              </h1>
              
              <p className="text-gray-600 mb-2">{auction.address}</p>
              <p className="text-gray-600 mb-6">{auction.cityName}, {auction.districtName}</p>

              {/* Müzayede Tarihleri */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 mb-1">{t('dates.startDate')}</p>
                    <p className="font-semibold text-gray-900">
                      {formatDateTime(auction.startDate, locale === 'tr' ? 'tr-TR' : 'en-US')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">{t('dates.endDate')}</p>
                    <p className="font-semibold text-gray-900">
                      {formatDateTime(auction.endDate, locale === 'tr' ? 'tr-TR' : 'en-US')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Current Bid Info - Sadece aktif müzayedeler için */}
              {auction.status === 2 && (
                <>
                  {/* Teklif varsa */}
                  {auction.totalBidCount > 0 && auction.currentHighestBid ? (
                    <div className={`border rounded-lg p-4 mb-6 ${
                      isHighestBidder 
                        ? 'bg-yellow-50 border-yellow-200' 
                        : 'bg-green-50 border-green-200'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className={`text-sm font-semibold ${
                              isHighestBidder ? 'text-yellow-700' : 'text-green-700'
                            }`}>
                              {t('bidding.highestBid')}
                            </p>
                          </div>
                          <p className={`text-2xl font-bold ${
                            isHighestBidder ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {formatPrice(auction.currentHighestBid)}
                          </p>
                          <p className={`text-xs ${
                            isHighestBidder ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {auction.totalBidCount} {t('bidding.bidCount')}
                          </p>
                          {isHighestBidder && (
                            <div className="mt-2">
                              <div className="bg-green-600 text-white text-sm px-3 py-1.5 rounded-md font-semibold shadow-md inline-flex items-center gap-1.5">
                                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                <span>{t('bidding.highestBidYou')}</span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 mb-1">{t('bidding.startingPrice')}</p>
                          <p className="text-lg font-bold text-gray-900">{formatPrice(auction.startingPrice)}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Henüz teklif yoksa */
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <div className="text-center">
                        <p className="text-sm font-semibold text-blue-700 mb-1">{t('bidding.startingPrice')}</p>
                        <p className="text-2xl font-bold text-blue-600">{formatPrice(auction.startingPrice)}</p>
                        <p className="text-xs text-blue-600 mt-1">{t('bidding.noBidsYet')}</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Starting Price Info - Yaklaşan müzayedeler için */}
              {auction.status === 1 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm font-semibold text-blue-700 mb-1">{t('upcoming.startingPrice')}</p>
                    <p className="text-2xl font-bold text-blue-600">{formatPrice(auction.startingPrice)}</p>
                    <p className="text-xs text-blue-600 mt-1">{t('upcoming.noBidsYet')}</p>
                  </div>
                </div>
              )}

              {/* Time Remaining - Aktif Müzayedeler */}
              {auction.status === 2 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">{t('bidding.timeRemaining')}</p>
                  <div className="flex items-center gap-2">
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600">{timeRemaining.days.toString().padStart(2, '0')}</div>
                      <div className="text-xs text-gray-600">{t('bidding.days')}</div>
                    </div>
                    <div className="text-xl font-bold text-red-600">:</div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600">{timeRemaining.hours.toString().padStart(2, '0')}</div>
                      <div className="text-xs text-gray-600">{t('bidding.hours')}</div>
                    </div>
                    <div className="text-xl font-bold text-red-600">:</div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600">{timeRemaining.minutes.toString().padStart(2, '0')}</div>
                      <div className="text-xs text-gray-600">{t('bidding.minutes')}</div>
                    </div>
                    <div className="text-xl font-bold text-red-600">:</div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-red-600">{timeRemaining.seconds.toString().padStart(2, '0')}</div>
                      <div className="text-xs text-gray-600">{t('bidding.seconds')}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Time Until Start - Yaklaşan Müzayedeler */}
              {auction.status === 1 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-blue-700 mb-3">{t('upcoming.timeUntilStart')}</p>
                  <div className="flex items-center gap-2">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">{timeUntilStart.days.toString().padStart(2, '0')}</div>
                      <div className="text-xs text-gray-600">{t('upcoming.days')}</div>
                    </div>
                    <div className="text-xl font-bold text-blue-600">:</div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">{timeUntilStart.hours.toString().padStart(2, '0')}</div>
                      <div className="text-xs text-gray-600">{t('upcoming.hours')}</div>
                    </div>
                    <div className="text-xl font-bold text-blue-600">:</div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">{timeUntilStart.minutes.toString().padStart(2, '0')}</div>
                      <div className="text-xs text-gray-600">{t('upcoming.minutes')}</div>
                    </div>
                    <div className="text-xl font-bold text-blue-600">:</div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">{timeUntilStart.seconds.toString().padStart(2, '0')}</div>
                      <div className="text-xs text-gray-600">{t('upcoming.seconds')}</div>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700 text-center">
                      {t('upcoming.waitingMessage')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Bidding Form - Sadece aktif müzayedeler için */}
            {auction.status === 2 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 sm:p-6 shadow-lg">
                <h3 className="text-lg font-bold text-red-600 text-center mb-4">{t('bidding.title')}</h3>
                
                {authState.isAuthenticated && authState.user?.isEDevletVerified === true && !authState.user?.roles?.includes('Admin') ? (
                  <form onSubmit={handleBidSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('bidding.yourBid')}
                      </label>
                      <input
                        type="text"
                        value={formatInputPrice(bidAmount)}
                        onChange={(e) => {
                          const numericValue = parseFormattedPrice(e.target.value);
                          if (numericValue >= 0) {
                            setBidAmount(numericValue);
                          }
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none text-2xl font-bold text-gray-900"
                        placeholder={t('bidding.amountPlaceholder')}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {auction.currentHighestBid 
                          ? `${t('bidding.minBid')} ${formatPrice(auction.currentHighestBid + auction.minimumIncrement)}`
                          : `${t('bidding.minBid')} ${formatPrice(auction.startingPrice + auction.minimumIncrement)}`
                        }
                      </p>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={
                        bidAmount < (auction.currentHighestBid ? (auction.currentHighestBid + auction.minimumIncrement) : auction.startingPrice + auction.minimumIncrement)
                      }
                      className={`w-full font-bold py-4 px-6 rounded-lg transition-colors shadow-lg ${
                        bidAmount >= (auction.currentHighestBid ? (auction.currentHighestBid + auction.minimumIncrement) : auction.startingPrice + auction.minimumIncrement)
                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {t('bidding.confirmBid')}
                    </button>
                    
                    <p className="text-center text-sm text-gray-600">
                      {t('bidding.minIncrement')} {formatPrice(auction.minimumIncrement)}
                    </p>
                  </form>
                ) : authState.isAuthenticated && authState.user?.isEDevletVerified !== true ? (
                  <div className="text-center space-y-4">
                    <div>
                      <p className="text-gray-600 mb-4">
                        {t('auth.edevletRequired')}
                      </p>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-yellow-800 font-medium">
                          {t('auth.securityRequired')}
                        </span>
                      </div>
                    </div>

                    <a
                      href={`/${locale}/hesabim?sekme=ayarlar`}
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                    >
                      {t('auth.edevletVerify')}
                    </a>
                    
                    <p className="text-xs text-gray-500">
                      {t('auth.edevletNote')}
                    </p>
                  </div>
                ) : authState.isAuthenticated && authState.user?.roles?.includes('Admin') ? (
                  <div className="text-center space-y-4">
                    <div>
                      <p className="text-gray-600 mb-4">
                        {t('bidding.adminRestriction')}
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-blue-800 font-medium">
                          {t('bidding.adminRestrictionTitle')}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500">
                      {t('bidding.adminRestrictionNote')}
                    </p>
                  </div>
                ) : (
                    <div className="text-center space-y-4">
                      <div>
                        <p className="text-gray-600 mb-4">
                          {t('auth.loginRequired')}
                        </p>
                      </div>
                    
                    <div className="flex gap-3">
                      <a
                        href={`/${locale}/giris`}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center"
                      >
                        {t('auth.login')}
                      </a>
                      <a
                        href={`/${locale}/kayit`}
                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center"
                      >
                        {t('auth.register')}
                      </a>
                    </div>
                    
                    <p className="text-xs text-gray-500">
                      {t('auth.loginRegisterNote')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Winner Info - Tamamlanmış müzayedeler için */}
            {auction.status === 4 && auction.winnerUserName && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 sm:p-6 shadow-lg">
                <h3 className="text-lg font-bold text-green-600 text-center mb-4">{t('winner.title')}</h3>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">{t('winner.winningBid')}</p>
                  <p className="text-2xl font-bold text-green-600 mb-2">
                    {auction.winningBidAmount ? formatPrice(auction.winningBidAmount) : t('winner.unknown')}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{t('winner.winner')}:</span> 
                    <span className="ml-1 font-mono tracking-wider">{auction.winnerUserName}</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bid Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center">
              {/* Confirmation Stage */}
              {!bidResult && (
                <>
                  {/* Icon */}
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                    <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {t('modal.confirmTitle')}
                  </h3>

                  {/* Content */}
                  <div className="mb-6">
                    <p className="text-gray-600 mb-4">
                      {t('modal.confirmMessage', { amount: formatPrice(bidAmount) })}
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-4 text-sm">
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">
                          {auction.currentHighestBid ? t('modal.currentHighestBid') : t('modal.startingPrice')}
                        </span>
                        <span className="font-semibold">
                          {auction.currentHighestBid ? formatPrice(auction.currentHighestBid) : formatPrice(auction.startingPrice)}
                        </span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">{t('modal.yourBid')}</span>
                        <span className="font-semibold text-red-600">{formatPrice(bidAmount)}</span>
                      </div>
                      {auction.currentHighestBid && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('modal.difference')}</span>
                          <span className="font-semibold text-green-600">
                            +{formatPrice(bidAmount - auction.currentHighestBid)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-4">
                    <button
                      onClick={handleCancelBid}
                      disabled={isSubmitting}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {t('modal.cancel')}
                    </button>
                    <button
                      onClick={handleConfirmBid}
                      disabled={isSubmitting}
                      className="flex-1 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('modal.submitting')}
                        </>
                      ) : (
                        t('modal.confirm')
                      )}
                    </button>
                  </div>

                  {/* Warning */}
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-medium text-red-800">
                        {t('modal.warning')}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* Loading Stage */}
              {bidResult === 'pending' && (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-6">
                    <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('modal.submitting')}</h3>
                  <p className="text-gray-600">{t('modal.submittingMessage')}</p>
                </>
              )}

              {/* Success Stage */}
              {bidResult === 'success' && (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                    <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-green-600 mb-4">{t('modal.success')}</h3>
                  <p className="text-gray-600 mb-6">{bidResultMessage}</p>
                  <div className="bg-green-50 rounded-lg p-4 mb-6">
                    <p className="text-sm font-semibold text-green-700">{t('modal.yourBidAmount', { amount: formatPrice(bidAmount) })}</p>
                  </div>
                  <button
                    onClick={handleCloseResultModal}
                    className="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {t('modal.ok')}
                  </button>
                </>
              )}

              {/* Error Stage */}
              {bidResult === 'error' && (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                    <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-red-600 mb-4">{t('modal.error')}</h3>
                  <p className="text-gray-600 mb-6">{bidResultMessage}</p>
                  <button
                    onClick={handleCloseResultModal}
                    className="w-full px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
                  >
                    {t('modal.close')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
